// Holdings, cost, realized and unrealized gains, income per holding and today's change, by the average
// cost method. Ported line for line from v1's compute(), totals(), allSales(), realizedIn(), heldQty()
// and heldFor().
/* eslint-disable @typescript-eslint/no-explicit-any */
import { now, today, yearAgoIso } from './clock';
import { N, cv, keyOf, tp, tq, type Price, type State } from './state';
import { heldPrice } from './bonds';

export interface Sale { date: string; type: string; sym: string; key: string; qty: number; proceeds: number; cost: number; gain: number; held: number }
export interface Position {
  key: string; type: string; sym: string; events: any[];
  divNet: number; divGross: number; divTax: number; stake: number; stakeQty: number; ttm: number;
  sales: Sale[]; qty: number; cost: number; realized: number; deployed: number; avg: number;
  pr: Price | null; price: number | null; mv: number; unreal: number; income: number; total: number;
  prevPx: number | null; day: number | null; dayBase: number; dayMissing: boolean;
}

export function compute(S: State): Position[] {
  const map: Record<string, any> = {};
  const get = (type: string, sym: string) => { const k = keyOf(type, sym); return map[k] || (map[k] = { key: k, type, sym, events: [], divNet: 0, divGross: 0, divTax: 0, stake: 0, stakeQty: 0, ttm: 0 }); };
  const yStr = yearAgoIso(), td = today();
  for (const t of S.txns) get(t.type, t.sym).events.push({ date: t.date, side: t.side, qty: tq(t), price: cv(S, tp(t), t.ccy), fee: cv(S, t.fee, t.ccy), o: t.side === 'buy' ? 0 : 1 });
  for (const i of S.income) {
    const p = get(i.type, i.sym), g = cv(S, i.gross, i.ccy);
    // v2: bond coupons count like dividends
    if (i.kind === 'dividend' || i.kind === 'coupon') { const net = g - cv(S, i.tax, i.ccy); p.divNet += net; p.divGross += g; p.divTax += cv(S, i.tax, i.ccy); if (i.date > yStr) p.ttm += net; }
    else {
      p.stake += g; p.stakeQty += N(i.qty); if (i.date > yStr) p.ttm += g;
      if (i.addToHoldings !== false && N(i.qty) > 0) p.events.push({ date: i.date, side: 'buy', qty: N(i.qty), price: g / N(i.qty), fee: 0, o: 0, reward: true });
    }
  }
  for (const p of Object.values<any>(map)) {
    p.events.sort((a: any, b: any) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.o - b.o));
    let q = 0, c = 0, r = 0, dep = 0, dw = 0, q0: number | null = null, flow = 0, buyIn = 0; const sales: Sale[] = [];
    for (const e of p.events) {
      if (q0 === null && e.date >= td) q0 = q;
      const t0 = Date.parse(e.date) || now();
      if (e.side === 'buy') { const amt = e.qty * e.price + e.fee; q += e.qty; c += amt; dw += e.qty * t0; if (!e.reward) dep += amt; if (e.date >= td) { flow += amt; buyIn += amt; } }
      else {
        const avg = q > 0 ? c / q : 0, sq = Math.min(e.qty, q), feeShare = e.qty > 0 ? e.fee * sq / e.qty : 0, proceeds = sq * e.price - feeShare, basis = avg * sq;
        if (sq > 0) { const held = q > 0 ? Math.max(0, (t0 - dw / q) / 864e5) : 0; sales.push({ date: e.date, type: p.type, sym: p.sym, key: p.key, qty: sq, proceeds, cost: basis, gain: proceeds - basis, held }); dw -= q > 0 ? dw / q * sq : 0; }
        r += proceeds - basis; c -= basis; q -= sq; if (e.date >= td) flow -= proceeds;
      }
    }
    if (q0 === null) q0 = q; if (Math.abs(q0) < 1e-10) q0 = 0;
    if (Math.abs(q) < 1e-10) { q = 0; c = 0; }
    p.sales = sales;
    p.qty = q; p.cost = c; p.realized = r; p.deployed = dep; p.avg = q > 0 ? c / q : 0;
    // v2: T-bills and Savings Bonds are valued by their own rule unless you've typed a price.
    const pr = p.type === 'bond' && !(S.prices[p.key] && S.prices[p.key].src === 'manual') ? heldPrice(S, p.sym) || S.prices[p.key] : S.prices[p.key]; p.pr = pr || null; p.price = pr ? cv(S, pr.price, pr.ccy) : null;
    p.mv = q > 0 ? (p.price != null ? q * p.price : c) : 0;
    p.unreal = q > 0 && p.price != null ? p.mv - c : 0;
    p.income = p.divNet + p.stake;
    p.total = p.unreal + p.realized + p.income;
    // change since the previous close: value now, minus value at the previous close, minus money put in today (net of sales)
    p.prevPx = pr && (pr.prev as number) > 0 && (!pr.pd || pr.pd === td) ? cv(S, pr.prev, pr.ccy) : null;
    const endV = q > 0 ? (p.price != null ? q * p.price : null) : 0, startV = (q0 as number) > 0 ? (p.prevPx != null ? (q0 as number) * p.prevPx : null) : 0;
    p.day = endV == null || startV == null ? null : endV - startV - flow; p.dayBase = (startV || 0) + buyIn;
    p.dayMissing = ((q0 as number) > 0 || q > 0) && p.day == null;
  }
  return Object.values<Position>(map).sort((a, b) => (+(b.qty > 0) - +(a.qty > 0)) || b.mv - a.mv || a.sym.localeCompare(b.sym));
}

export interface Totals { mv: number; cost: number; unreal: number; realized: number; div: number; stake: number; deployed: number; missing: number; stockMv: number; cryptoMv: number; metalMv: number; ret: number; [k: string]: number }
export function totals(ps: Position[], type?: string): Totals {
  const t: any = { mv: 0, cost: 0, unreal: 0, realized: 0, div: 0, stake: 0, deployed: 0, missing: 0, stockMv: 0, cryptoMv: 0, metalMv: 0 };   // bondMv appears when there are bonds
  for (const p of ps) {
    if (type && p.type !== type) continue;
    t.mv += p.mv; t.cost += p.cost; t.unreal += p.unreal; t.realized += p.realized; t.div += p.divNet; t.stake += p.stake; t.deployed += p.deployed;
    if (p.qty > 0 && p.price == null) t.missing++;
    t[p.type + 'Mv'] = (t[p.type + 'Mv'] || 0) + p.mv;
  }
  t.ret = t.unreal + t.realized + t.div + t.stake;
  return t;
}

export const allSales = (ps: Position[]): Sale[] => ps.flatMap((p) => p.sales || []).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
export function realizedIn(ps: Position[], year: string): number { return allSales(ps).filter((x) => x.date.startsWith(year)).reduce((a, x) => a + x.gain, 0); }

export function heldQty(S: State, type: string, sym: string, excludeId?: string): number {
  let q = 0;
  for (const t of S.txns) if (t.type === type && t.sym === sym && t.id !== excludeId) q += t.side === 'buy' ? tq(t) : -tq(t);
  for (const i of S.income) if (i.kind === 'staking' && i.type === type && i.sym === sym && i.addToHoldings !== false && i.id !== excludeId) q += N(i.qty);
  return q;
}

export const heldFor = (d: number): string => {
  d = Math.round(d); if (d < 60) return d + (d === 1 ? ' day' : ' days');
  const m = Math.round(d / 30.44); if (m < 12) return m + ' months';
  const y = Math.floor(m / 12), r = m % 12; return y + (y > 1 ? ' yrs' : ' yr') + (r ? ' ' + r + ' mo' : '');
};
